import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Col, Input, Row, Table } from "reactstrap";
import { FilterSelect, ServiceThreadSpinner } from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, filterHelpers, UserContext } from "../utils";
import _ from "lodash";

export default function MobileRwoNew(props) {
   const navigate = useNavigate();
   const { id } = useParams();
   const { currentUser } = useContext(UserContext);
   const ALL_OPTION = { value: 0, label: "N/A" };
   const NA_OPTION = { value: "N/A", label: "N/A" };
   const ALL_PROBLEMS = { value: 0, label: "-- Select --" };
   const ALL_MACHINES = {
      value: 0,
      label: "-- Select --",
      activeMachines: "0",
      spindles: 0,
      sides: false,
      sendMail: false,
   };
   const [filters, setFilters] = useState([
      { filterName: "machineId", value: ALL_MACHINES },
      { filterName: "machineNum", value: ALL_OPTION },
      { filterName: "spindle", value: ALL_OPTION },
      { filterName: "side", value: NA_OPTION },
      { filterName: "problemId", value: ALL_PROBLEMS },
      { filterName: "problemSubId", value: ALL_OPTION },
   ]);
   const [data, setData] = useState(null);
   const [loaded, setLoaded] = useState(false);
   const [machines, setMachines] = useState([]);
   const [machineNums, setMachineNums] = useState([]);
   const [noEdit, setDisabled] = useState(true);
   const [problems, setProblems] = useState([]);
   const [problemSubs, setProblemSubs] = useState([]); // problem children filtered by parent problem id
   const [problemTemps, setProblemTemps] = useState([]); // problem children (subs) unfiltered
   const [sides, setSides] = useState([]);
   const [spindles, setSpindles] = useState([]);
   const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

   useEffect(() => {
      if (!currentUser) return;
      var active = true; // Indicator of if we only show active problems and their children

      // Get possible drop-down options
      api
         .fetch(`Repair/LoadRwoDropdowns/${active}`)
         .then((response) => {
            if (response.data.success) {
               const machineList = _.concat(
                  [ALL_MACHINES],
                  _.map(response.data.machines, (m) => ({
                     value: m.machineId,
                     label: m.machineName,
                     activeMachines: m.activeMachines,
                     spindles: m.spindlesPerSide,
                     sides: m.hasBSide,
                     sendMail: m.sendMail,
                  }))
               );
               const problemList = _.concat(
                  [ALL_PROBLEMS],
                  _.map(response.data.problems, (pblm) => ({
                     value: pblm.problemId,
                     label: pblm.problem,
                  }))
               );
               const problemSubList = _.concat(
                  _.map(response.data.problemSubs, (pSub) => ({
                     value: pSub.id,
                     label: pSub.problem,
                     parent: pSub.problemId,
                  }))
               );
               return {
                  machineList: machineList,
                  problemList: problemList,
                  problemSubList: problemSubList,
               };
            } else {
               throw new Error(response);
            }
         })
         .then(({ machineList, problemList, problemSubList }) => {
            setMachines(machineList);
            setProblems(problemList);
            setProblemTemps(problemSubList);
            // These should only fill in after a machine is selected, just put in a default selections
            setMachineNums([ALL_OPTION]);
            setSides([NA_OPTION]);
            setSpindles([ALL_OPTION]);
            // This should only fill in after a parent problem is selected
            setProblemSubs({ value: 0, label: "N/A", parent: 0 });
            runQuery();
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentUser]);

   useEffect(() => {
      async function setFields() {
         await sleep(50); // allows time for input to be recognized
         if (!loaded && data) {
            if (data.machineTypeId > 0) {
               // populate child drop-downs as appropriate for the selected rwo machine
               const mach = _.find(machines, (x) => x.value === data.machineTypeId);
               await fixMachines(mach.activeMachines);
               await fixSpindles(mach.spindles);
               await fixSides(mach.sides);

               // Set ddl selected values
               let newFilters = filterHelpers.getFilters(filters, {
                  filterName: "machineId",
                  value: mach,
               });
               setFilters(newFilters);
            }
            setLoaded(true);
         }
      }
      setFields();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data]);

   function runQuery() {
      // This initializes the rwo
      api
         .post(`Repair/GetRepair/0`)
         .then((response) => {
            if (response.data.success) {
               setDisabled(!response.data.canEdit);
               response.data.rwo.machineTypeId = id > 0 ? parseInt(id) : 0;
               setData(response.data.rwo);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         });
   }

   function onChange(field, value) {
      let changes = Object.assign({}, data);
      changes[field] = value;
      setData(changes);
   }

   // These are related to the machine type selection change and its sub filters
   const onMachineChange = async (newFilterValue) => {
      // Fill in the machine related drop-down lists with proper values
      const mach = _.find(machines, (x) => x.value === newFilterValue.value);
      let newMachList = await fixMachines(mach.activeMachines);
      let newSpindleList = await fixSpindles(mach.spindles);
      let newSideList = await fixSides(mach.sides);

      // Set the machine dependent filters to their defaults
      const numFilter = _.find(newMachList, (m) => m.value === 0);
      const spindleFilter = _.find(newSpindleList, (s) => s.value === 0);
      const sideFilter = _.find(newSideList, (s) => s.value === "N/A");

      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "machineId",
         value: mach,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "machineNum",
         value: numFilter,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "spindle",
         value: spindleFilter,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "side",
         value: sideFilter,
      });
      setFilters(newFilters);

      // Set the data values
      let changes = Object.assign({}, data);
      changes.machineTypeId = newFilterValue.value;
      changes.machineName = mach.label;
      changes.machineNumber = ALL_OPTION.value;
      changes.machineSide = NA_OPTION.value;
      changes.spindle = ALL_OPTION.value;
      setData(changes);
   };

   const fixMachines = async (activeMachines) => {
      // Fill in the drop-down with active machines for the selected machine
      var nums = activeMachines.split(", ");
      let newMachineList = _.concat(
         [ALL_OPTION],
         _.map(nums, (m) => ({ value: parseInt(m, 10), label: m }))
      );
      setMachineNums(newMachineList);
      return newMachineList;
   };

   const fixSpindles = async (spindlesPerSide) => {
      // Fill in the drop-down with available spindles for the selected machine
      let newSpindleList = [ALL_OPTION];
      for (let i = 1; i <= spindlesPerSide; i++) {
         newSpindleList = _.concat(
            newSpindleList,
            _.map([{ value: i, label: i.toString() }])
         );
      }
      setSpindles(newSpindleList);
      return newSpindleList;
   };

   const fixSides = async (hasBSide) => {
      // Fill in the drop-down with available sides for the selected machine
      let newSideList = [NA_OPTION];
      if (hasBSide === true) {
         newSideList = _.concat(newSideList, _.map([{ value: "A", label: "A" }]));
         newSideList = _.concat(newSideList, _.map([{ value: "B", label: "B" }]));
      }
      setSides(newSideList);
      return newSideList;
   };

   // These are related to the problem type selection change and its sub filter
   const onProblemChange = async (newFilterValue) => {
      // Fill in the problem child drop-down with proper values
      const pblm = _.find(problems, (x) => x.value === newFilterValue.value);
      let newSubList = await fixProblemSubs(newFilterValue.value);

      // Set the problem filters
      var subs = _.pickBy(problemTemps, (e) =>
         _.includes([newFilterValue.value], e.parent)
      );
      const subFilter = _.find(newSubList, (s) => s.value === 0);

      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "problemId",
         value: pblm,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "problemSubId",
         value: subFilter,
      });
      setFilters(newFilters);

      // Set the data values
      let changes = Object.assign({}, data);
      changes.problemId = newFilterValue.value;
      changes.problem = pblm.label;
      changes.problemSubId = subs ? ALL_PROBLEMS.value : ALL_OPTION.value;
      changes.problemChild = "";
      setData(changes);
   };

   const fixProblemSubs = async (id) => {
      let newPblmList = [ALL_OPTION]; // If no children found then N/A is the default option
      var subs = _.pickBy(problemTemps, (e) => _.includes([id], e.parent));
      if (subs) {
         // There are child problems for the selected problem type
         newPblmList = _.concat(
            [ALL_PROBLEMS],
            _.map(subs, (s) => ({ value: s.value, label: s.label }))
         );
         setProblemSubs(newPblmList);
      }
      return newPblmList;
   };

   // These are selection changes without sub filters
   function onMachineNumChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, data);
      changes.machineNumber = newFilterValue.value;
      setData(changes);
   }

   function onSideChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, data);
      changes.machineSide = newFilterValue.value;
      setData(changes);
   }

   function onSpindleChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, data);
      changes.spindle = newFilterValue.value;
      setData(changes);
   }

   function onProblemSubChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      const pblm = _.find(problemSubs, (x) => x.value === newFilterValue.value);
      let changes = Object.assign({}, data);
      changes.problemSubId = newFilterValue.value;
      changes.problemChild = pblm.label;
      setData(changes);
   }

   // Save changes to database    -- Can I enforce the spindle selection if filter has more than one option?
   function saveWorkOrder() {
      let warnings = [];

      if (!data.machineTypeId) {
         warnings.push("Machine is required\n");
      }
      if (!data.machineNumber) {
         warnings.push("Machine # is required\n");
      }
      if (!data.machineSide) {
         warnings.push("Side is required\n");
      }
      //if (!data.spindle && spindles.length > 1) {
      //  warnings.push('Spindle is required\n');
      //}
      if (!data.problemId) {
         warnings.push("Problem is required\n");
      }
      if (data.problemId && problemSubs.length > 1 && !data.problemSubId) {
         warnings.push("Child problem is required\n");
      }
      if (!data.operatorInitials) {
         warnings.push("Lead person initials are required\n");
      }
      if (warnings.length) {
         toast.error(warnings.join(''));
         return;
      }

      api
         .post(`Repair/CreateRepairWorkOrder`, data)
         .then((response) => {
            if (response.data.success) {
               toast.success("New work order was created successfully!");
               // Saved, but the maintenance email failed - say so, so nobody re-submits a saved RWO
               if (response.data.emailWarning)
                  toast.warning(response.data.emailWarning, { autoClose: false });
               navigate(constants.ENDPOINTS.mobileRwoSearch);
            } else {
               toast.error(response.data.message);
            }
         })
         .catch(api.catchHandler);
   }

   return (
      <DesktopContainer screenName="Repair Work Order">
         <ServiceThreadSpinner on={!loaded}>
            <div>
               <Row>
                  <Col xs={12}>
                     <label className="h6 fw-bold">
                        Work Order #: &nbsp;&nbsp;Determined upon save
                     </label>
                  </Col>
               </Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Machine Type:
                  </Col>
               </Row>
               <Row className="align-items-center my-1">
                  <Col xs={12}>
                     <FilterSelect
                        filterName="machineId"
                        options={machines}
                        value={filterHelpers.getValue(filters, "machineId")}
                        onChangeCallback={(e) => onMachineChange(e)}
                     />
                  </Col>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Machine #:
                  </Col>
               </Row>
               <Row className="align-items-center my-1">
                  <Col xs={12}>
                     <FilterSelect
                        filterName="machineNum"
                        options={machineNums}
                        value={filterHelpers.getValue(filters, "machineNum")}
                        onChangeCallback={(e) => onMachineNumChange(e)}
                     />
                  </Col>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Side:
                  </Col>
               </Row>
               <Row className="align-items-center my-1">
                  <Col xs={12}>
                     <FilterSelect
                        filterName="side"
                        options={sides}
                        value={filterHelpers.getValue(filters, "side")}
                        onChangeCallback={(e) => onSideChange(e)}
                     />
                  </Col>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Spindle #:
                  </Col>
               </Row>
               <Row className="align-items-center my-1">
                  <Col xs={12}>
                     <FilterSelect
                        filterName="spindle"
                        options={spindles}
                        value={filterHelpers.getValue(filters, "spindle")}
                        onChangeCallback={(e) => onSpindleChange(e)}
                     />
                  </Col>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Problem:
                  </Col>
               </Row>
               <Row className="align-items-center my-1">
                  <Col xs={12}>
                     <FilterSelect
                        filterName="problemId"
                        options={problems}
                        value={filterHelpers.getValue(filters, "problemId")}
                        onChangeCallback={(e) => onProblemChange(e)}
                     />
                  </Col>
               </Row>
               {problemSubs && problemSubs.length >= 1 ? (
                  <div>
                     <Row className="lineheight8">&nbsp;</Row>
                     <Row className="align-items-center my-1">
                        <Col className="h6 fw-bold my-0" xs={12}>
                           Child Problem:
                        </Col>
                     </Row>
                     <Row className="align-items-center my-1">
                        <Col xs={12}>
                           <FilterSelect
                              filterName="problemSubId"
                              options={problemSubs}
                              value={filterHelpers.getValue(filters, "problemSubId")}
                              onChangeCallback={(e) => onProblemSubChange(e)}
                           />
                        </Col>
                     </Row>
                  </div>
               ) : null}
               <Row className="lineheight8">&nbsp;</Row>
               <Row>
                  <Col xs={12} className="py-0 px-3">
                     <label className="h6 fw-bold my-0">Lead Initials:</label>&nbsp;
                     <Input
                        className="w-25"
                        name="operatorInitials"
                        type="text"
                        maxLength={2}
                        value={
                           data && data.operatorInitials ? data.operatorInitials : ""
                        }
                        onChange={(e) => {
                           onChange("operatorInitials", e.target.value);
                        }}
                     />
                  </Col>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row className="align-items-center my-1">
                  <Col className="h6 fw-bold my-0" xs={12}>
                     Additional Comments:
                  </Col>
               </Row>
               <Row className="align-items-center my-1">
                  <Col xs={12}>
                     <Input
                        type="textarea"
                        className="serviceThreadTextArea"
                        value={(data && data.comments) ?? ""}
                        maxLength={500}
                        onChange={(e) => {
                           onChange("comments", e.target.value);
                        }}
                     />
                  </Col>
               </Row>
               {!noEdit ? (
                  <div className="blueBox w-100 my-3">
                     <Row>
                        <Col xs={12}>
                           <h2 align="center" className="text-white mobileBlueBgAlt">
                              Mechanics Comments
                           </h2>
                        </Col>
                     </Row>
                     <Table borderless size="sm">
                        <tbody>
                           <tr>
                              <td className="data-cell my-0 py-1 px-3">
                                 <label className="h6 fw-bold">
                                    <Input
                                       name="closed"
                                       type="checkbox"
                                       className="me-2"
                                       checked={data && data.closed}
                                       onChange={(e) => {
                                          onChange("closed", e.target.checked);
                                       }}
                                    />{" "}
                                    Repair Complete?
                                 </label>
                              </td>
                           </tr>
                           <tr>
                              <td className="data-cell m-0 p-0 lineheight8">
                                 &nbsp;
                              </td>
                           </tr>
                           <tr>
                              <td className="data-cell my-0 py-0 px-3">
                                 <label className="h6 fw-bold my-0">Tech Initials:</label>
                                 <Input
                                    className="w-25"
                                    name="mechanicInitials"
                                    type="text"
                                    maxLength={2}
                                    value={
                                       data && data.mechanicInitials
                                          ? data.mechanicInitials
                                          : ""
                                    }
                                    onChange={(e) => {
                                       onChange("mechanicInitials", e.target.value);
                                    }}
                                 />
                              </td>
                           </tr>
                           <tr>
                              <td className="data-cell m-0 p-0 lineheight8">
                                 &nbsp;
                              </td>
                           </tr>
                           <tr>
                              <td className="data-cell my-0 py-0 px-3">
                                 <label className="h6 fw-bold mx-1 my-0">
                                    Repair Comments:
                                 </label>
                              </td>
                           </tr>
                           <tr>
                              <td className="data-cell my-0 py-0 px-3">
                                 <Input
                                    className="serviceThreadTextArea"
                                    type="textarea"
                                    name="repairComments"
                                    value={data?.repairComments ?? ""}
                                    maxLength={500}
                                    onChange={(e) => {
                                       onChange("repairComments", e.target.value);
                                    }}
                                 />
                              </td>
                           </tr>
                        </tbody>
                     </Table>
                  </div>
               ) : null}
               <Row className="lineheight8">&nbsp;</Row>
               <Row>
                  <Button
                     title="Save"
                     className="shredButton ms-2 btn-md"
                     onClick={() => saveWorkOrder()}
                  >
                     Save
                  </Button>
               </Row>
               <Row className="lineheight8">&nbsp;</Row>
               <Row>
                  <Button
                     title="To Search"
                     className="btn ms-2 btn-md"
                     onClick={() => navigate(`/mobile-rwo-search`)}
                  >
                     To Search
                  </Button>
               </Row>
               <br />
            </div>
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
